import { Box, Link as MuiLink } from "@mui/material";
import { styled } from "@mui/material/styles";
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useEffect } from "react";

import type { ConnectorTile } from "@/types/connectors";

import { useHydratedConnectors } from "@/api/connectors/index";
import { useDragonTilesByType } from "@/api/dragon-tiles";
import { LoadingFallback } from "@/components/LoadingFallback";
import { AddConnectorTile } from "@/features/Connectors/AddConnectorTile";
import { ConnectorTileLogo } from "@/features/Connectors/ConnectorTileLogo";
import { getVisibleConnectorDisplayTiles } from "@/features/Connectors/connectorTileUtils";
import { TilesCountHexagon } from "@/features/Connectors/TilesCountHexagon";
import { HoneycombGrid } from "@/features/DragonTiles/components/HoneycombGrid/HoneycombGrid";
import { ParentTile } from "@/features/DragonTiles/components/ParentTile/ParentTile";

const HoneycombShell = styled("div")({
  position: "relative",
  width: "100%",
  height: "100%",
  "& .connector-back-link": {
    position: "absolute",
    top: 20,
    right: 16,
    zIndex: 1200,
    textDecoration: "none",
  },
  "@media (min-width: 1301px) and (max-width: 1600px)": {
    "& .connector-back-link": {
      top: 56,
    },
  },
});

const ADD_CONNECTOR_TILE = "add-connector";

type ConnectorGridTile = ConnectorTile | typeof ADD_CONNECTOR_TILE;

export const Route = createFileRoute("/_auth/dragon-tiles/$siteId/connectors/")(
  {
    component: RouteComponent,
  },
);

function RouteComponent() {
  return <ConnectorsPageContent />;
}

export function ConnectorsPageContent() {
  const { enqueueSnackbar } = useSnackbar();
  const router = useRouter();
  const { siteId } = useParams({
    from: "/_auth/dragon-tiles/$siteId/connectors",
  });
  const { data: connectors = [], isLoading } = useHydratedConnectors(siteId);
  const { data: composedConnectorTiles } = useDragonTilesByType(
    siteId,
    "connector",
  );
  const composedTileById = new Map(
    (composedConnectorTiles ?? []).map((tile) => [tile.id, tile]),
  );
  const dragonTileIdByConnectorId = new Map(
    connectors.flatMap((connector) =>
      connector.dragonTile
        ? [[connector.connectorId, connector.dragonTile.id]]
        : [],
    ),
  );
  const connectorTiles = getVisibleConnectorDisplayTiles(connectors);
  const gridTiles: ConnectorGridTile[] = [
    ...connectorTiles,
    ADD_CONNECTOR_TILE,
  ];

  useEffect(() => {
    if (isLoading || connectorTiles.length > 0) {
      return;
    }

    // Installed connectors can still be absent from this grid — a tile-less
    // data hub, or a tiled connector with no events, is installed but has
    // nothing to show here. Saying "none installed" would be false.
    enqueueSnackbar(
      connectors.length > 0
        ? "No connectors with events to show."
        : "No connectors installed.",
      {
        variant: "info",
        action: () => (
          <MuiLink
            component="button"
            onClick={() => {
              void router.navigate({
                to: "/settings/$siteId/connectors/all",
                params: { siteId },
              });
            }}
            sx={{
              border: "none",
              background: "none",
              color: "info.dark",
              cursor: "pointer",
              p: 0,
              m: 0,
              font: "inherit",
              textDecoration: "underline",
              whiteSpace: "nowrap",
            }}
          >
            Install your connectors now
          </MuiLink>
        ),
      },
    );
  }, [
    enqueueSnackbar,
    isLoading,
    router,
    siteId,
    connectorTiles.length,
    connectors.length,
  ]);

  if (isLoading) {
    return <LoadingFallback />;
  }

  const renderConnectorTile = (tile: ConnectorGridTile) => {
    if (tile === ADD_CONNECTOR_TILE) {
      return <AddConnectorTile siteId={siteId} />;
    }
    const dragonTileId = dragonTileIdByConnectorId.get(tile.id);
    const composedTile =
      dragonTileId === undefined
        ? undefined
        : composedTileById.get(dragonTileId);
    return (
      <ParentTile
        tile={
          composedTile
            ? { ...tile, inner_tiles: composedTile.inner_tiles }
            : tile
        }
        siteId={siteId}
        variant="large"
        framesLoaded={composedTile !== undefined}
        events={tile.events?.map(({ id, name, enabled }) => ({
          id,
          name,
          enabled: enabled ?? false,
        }))}
        linkTo="/dragon-tiles/$siteId/connectors/$parentTile"
        linkParams={{
          siteId,
          parentTile: tile.id,
        }}
        topAdornment={
          <TilesCountHexagon
            count={tile.events?.filter((event) => event.enabled).length ?? 0}
            events={tile.events}
          />
        }
        mediaContent={
          <ConnectorTileLogo name={tile.name} logoUrl={tile.logoUrl} />
        }
      />
    );
  };

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <HoneycombShell>
        <HoneycombGrid tiles={gridTiles} renderTile={renderConnectorTile} />
      </HoneycombShell>
    </Box>
  );
}
